import type { Criterion } from './types';

const round2 = (n: number) => Math.round(n * 100) / 100;

/** True when every criterion in the set carries a weight. */
export function isWeighted(criteria: Criterion[]): boolean {
  return criteria.every((c) => typeof c.weight === 'number');
}

/**
 * Authoritative total for a set of raw criterion values.
 * - Weighted sets (oral/poster): sum(weight * value), rounded to 2dp -> 0..5 scale.
 * - Unweighted sets: raw sum of values.
 * Missing/blank values count as 0. This mirrors the original app's math.
 */
export function computeTotal(criteria: Criterion[], values: Record<string, number>): number {
  if (isWeighted(criteria)) {
    const total = criteria.reduce((sum, c) => sum + (c.weight ?? 0) * (values[c.id] || 0), 0);
    return round2(total);
  }
  return criteria.reduce((sum, c) => sum + (values[c.id] || 0), 0);
}

/** The maximum a total can reach for a set (5 for weighted oral/poster, else sum of maxScores). */
export function computeMaxScore(criteria: Criterion[]): number {
  if (isWeighted(criteria)) {
    return round2(criteria.reduce((sum, c) => sum + (c.weight ?? 0) * c.maxScore, 0));
  }
  return criteria.reduce((sum, c) => sum + c.maxScore, 0);
}

/** Every criterion has a non-zero value selected. */
export function isComplete(criteria: Criterion[], values: Record<string, number>): boolean {
  return criteria.every((c) => (values[c.id] || 0) > 0);
}

/** Qualitative band used in the UI, recovered from the original ("Below 3.0: Needs Improvement"). */
export function scoreBand(total: number): { label: string; color: string } {
  if (total >= 4.5) return { label: 'Outstanding', color: '#10B981' };
  if (total >= 4.0) return { label: 'Excellent', color: '#22C55E' };
  if (total >= 3.5) return { label: 'Strong', color: '#2563EB' };
  if (total >= 3.0) return { label: 'Good', color: '#0D9488' };
  return { label: 'Needs Improvement', color: '#F59E0B' };
}
